import dayjs, { dateInputFieldFormat, formatDateString } from '~libs/dayjs';

import { BulkUpdateRow } from './contributionSelection';

export interface BulkUpdateError {
  id: string;
  client_id: number | null;
  category_name: string | null;
  reason: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `value` is a complete `YYYY-MM-DD` date that really exists. */
export const isValidEffectiveDate = (value: unknown): value is string =>
  typeof value === 'string' && ISO_DATE.test(value) && dayjs(value, dateInputFieldFormat, true).isValid();

/**
 * The new percentage as a number, or null while the input is blank, not a number or negative.
 * (The input is kept as text so a half-typed value like "1." is not rewritten under the cursor.)
 */
export const parseProportion = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/** True when the row starts on or after the effective date, so it is changed where it stands rather than split. */
export const isUpdatedInPlace = (row: BulkUpdateRow, effectiveDate: string): boolean =>
  row.start_date.slice(0, 10) >= effectiveDate;

/** What happens to a row: split into a new rate from the date, or changed where it stands. */
export const getRowNote = (row: BulkUpdateRow, effectiveDate: string): string => {
  const from = formatDateString(effectiveDate);
  if (!isUpdatedInPlace(row, effectiveDate)) {
    const lastDay = formatDateString(dayjs(effectiveDate).subtract(1, 'day').format(dateInputFieldFormat));
    return `New rate from ${from}; current rate ends ${lastDay}`;
  }
  return `Updated in place (starts on or after ${from})`;
};

/** Why a row cannot take the change: the full reason, plus a short form for the preview's Rate cell. */
export interface RowFlag {
  kind: 'over-max' | 'ends-before';
  reason: string;
  short: string;
}

/**
 * Why a row cannot take the new percentage from `effectiveDate`, or null when it can. Mirrors the API's
 * `getRejectionReason`, in the same order: over the category maximum, then ending before the date.
 * (Ended rows can't be selected, so the API's "already ended" case never reaches the preview.)
 */
export const getRowFlag = (
  row: BulkUpdateRow,
  proportion: number | null,
  effectiveDate: string | null,
): RowFlag | null => {
  if (proportion !== null && row.category_max !== null && proportion > row.category_max) {
    return {
      kind: 'over-max',
      reason: `${proportion}% is over the ${row.category_name} maximum of ${row.category_max}%`,
      short: `max ${row.category_max}%`,
    };
  }

  // YYYY-MM-DD strings compare as dates.
  const endDate = row.end_date?.slice(0, 10);
  if (effectiveDate !== null && endDate && endDate < effectiveDate) {
    return {
      kind: 'ends-before',
      reason: `This contribution ended before ${formatDateString(effectiveDate)}`,
      short: `ends ${formatDateString(endDate)}`,
    };
  }

  return null;
};

/**
 * The per-row reasons from a rejected bulk update. `rtkBaseQueryWrapper` keeps the whole response
 * body on the error as `data`, so with the API's `{ success, message, data: { errors } }` body the
 * list sits at `error.data.data.errors`. A bare `{ errors }` body is read too. Anything else,
 * including a 400 for a bad body (message only), gives an empty list.
 */
export const getBulkUpdateErrors = (error: unknown): BulkUpdateError[] => {
  const body = (error as { data?: any } | null)?.data;
  const errors = body?.data?.errors ?? body?.errors;
  if (!Array.isArray(errors)) return [];

  return errors
    .filter((item) => item && typeof item === 'object' && typeof item.reason === 'string')
    .map((item) => ({
      id: String(item.id),
      client_id: item.client_id ?? null,
      category_name: item.category_name ?? null,
      reason: item.reason,
    }));
};
